import {
    defineProductList,
    defineCart,
    defineSearchBox,
    defineContext,
    defineRecentQueriesList,
    defineNotifyTrigger,
    defineQueryTrigger,
    defineRedirectionTrigger,
    defineStandaloneSearchBox,
    defineInstantProducts,
    defineBreadcrumbManager,
    defineSummary,
    definePagination,
    defineFacetGenerator,
    defineSort,
    defineProductView,
    defineDidYouMean,
    defineFilterSuggestionsGenerator,
    defineParameterManager,
    defineCommerceEngine,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
} from "@coveo/headless-react/ssr-commerce";
import { getCoveoToken } from "@NA/Services/CoveoApiService";
import { CustomHandler } from "@NA/Store/CustomHandlerCreator/CustomHandlerCreator";
import { CoveoActions } from "@NA/Store/Reducer/CoveoCommerceReducer";
import { loadCartItemsFromStorage } from "@NA/Utils/Coveo/CoveoUtils";
import {
    createHandlerChainRunnerOptionalParameter,
    executeAwaitableHandlerChain,
} from "@insite/client-framework/HandlerCreator";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadCoveoStaticState from "./CoveoLoadStaticState";

type HandlerType = CustomHandler<{}>;

const DispatchBeginLoadCoveoEngine: HandlerType = ({ dispatch }) => {
    dispatch({
        type: CoveoActions.BEGIN_LOAD_ENGINE,
    });
};

const DispatchLoadCoveoToken: HandlerType = async ({ dispatch, getState }) => {
    const state = getState();
    if (!state.ADIGlobalData?.coveoCommerce) {
        dispatch({
            type: CoveoActions.BEGIN_LOAD_ENGINE,
        });
    }
    
    const token = state.ADIGlobalData?.coveoCommerce?.token;
    if (!token) {
        const coveoToken = await getCoveoToken();
        dispatch({
            type: CoveoActions.LOAD_TOKEN,
            token: coveoToken,
        });
    }
};

const DispatchLoadCoveoEngine: HandlerType = async ({ dispatch, getState }) => {
    const state = getState();
    const token = state.ADIGlobalData.coveoCommerce.token;
    const searchPath = getSettingsCollection(state).searchSettings.searchPath;
    const coveoPageSize = 28;

    if (token) {
        const engineDefinition = defineCommerceEngine({
            configuration: {
                organizationId: token.coveoOrgId,
                accessToken: token.token,
                context: {
                    language: token.language.split("-")[0],
                    currency: token.currency,
                    country: token.country,
                    view: {
                        url: token.commerceView,
                    },
                },
                analytics: {
                    trackingId: token.trackingId,
                },
                cart: {
                    items: loadCartItemsFromStorage() ?? []
                }
            },
            controllers: {
                summary: defineSummary(),
                productList: defineProductList(),
                cart: defineCart(),
                searchBox: defineSearchBox(),
                context: defineContext(),
                recentQueriesList: defineRecentQueriesList(),
                notifyTrigger: defineNotifyTrigger(),
                queryTrigger: defineQueryTrigger(),
                redirectionTrigger: defineRedirectionTrigger(),
                standaloneSearchBox: defineStandaloneSearchBox({
                    options: { redirectionUrl: searchPath },
                }),
                instantProducts: defineInstantProducts(),
                pagination: definePagination({
                    options: { pageSize: coveoPageSize },
                }),
                sort: defineSort(),
                productView: defineProductView(),
                didYouMean: defineDidYouMean(),
                parameterManager: defineParameterManager(),
                facetGenerator: defineFacetGenerator(),
                breadcrumbManager: defineBreadcrumbManager(),
                filterSuggestionsGenerator: defineFilterSuggestionsGenerator(),
            },
        });

        dispatch({
            type: CoveoActions.LOAD_ENGINE,
            engineDefinition: engineDefinition,
            isEngineLoadedOnClient: !IS_SERVER_SIDE,
        });
    }
};

const DispatchLoadCoveoStaticState: HandlerType = async props => {
    await executeAwaitableHandlerChain(loadCoveoStaticState, {}, props);
};

const DispatchCompleteLoadCoveoEngine: HandlerType = ({ dispatch }) => {
    dispatch({
        type: CoveoActions.COMPLETE_LOAD_ENGINE,
    });
};

export const chain = [
    DispatchBeginLoadCoveoEngine,
    DispatchLoadCoveoToken,
    DispatchLoadCoveoEngine,
    DispatchLoadCoveoStaticState,
    DispatchCompleteLoadCoveoEngine,
];

const loadCoveoEngine = createHandlerChainRunnerOptionalParameter(chain, {}, "LoadCoveoEngine");
export default loadCoveoEngine;
